import React from 'react'
import { Header, HTMLText } from '@crossfit/ui'

import { CompeteDailyClassPlanContent } from 'pages/Programming/types'
import Section from 'pages/Programming/components/Section'
import { useStrings } from 'components/Settings'
import DailyClassPlanHeader from './Header'
import style from './style.module.css'
import { trackEvent } from 'services/segment'

type Props = {
  classPlan: CompeteDailyClassPlanContent
}

function CompeteDailyClassPlan({ classPlan }: Props) {
  const strings = useStrings()
  const {
    type,
    name,
    dailyFocus,
    dailyTip,
    warmUp,
    workouts,
    stretching,
    workoutOrder,
  } = classPlan

  const onCopy = (e) => {
    trackEvent({
      component: 'Compete Daily Class plan',
      event: 'Compete Daily Class plan Copied',
      location: 'Affiliate Toolkit',
      text: e.target.innerText,
    })
  }

  return (
    <main className={style.main} onCopy={onCopy}>
      <Section className={style.section}>
        <DailyClassPlanHeader name={name} type={type} />

        <div className={style.content}>
          {workoutOrder && (
            <div className={style.oneThirdContent}>
              <Header size='medium' as='h3' mb={4}>
                {strings.workoutOrderText}
              </Header>
              <table className={style.table}>
                <thead>
                  <tr>
                    <th>&nbsp;</th>
                    <th>Element</th>
                    <th>Time Estimate</th>
                  </tr>
                </thead>
                <tbody>
                  {workoutOrder.map((item, index) => (
                    <tr key={item.element}>
                      <td className={style.number}>{index + 1}</td>
                      <td className={style.element}>{item.element}</td>
                      <td>{item.timeEstimate} minutes</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className={style.twoThirdContent}>
            {dailyFocus && (
              <>
                <Header size='small' as='h3' mb={2}>
                  {strings.dailyFocusText}
                </Header>
                <HTMLText>{dailyFocus}</HTMLText>
              </>
            )}
            {dailyTip && (
              <>
                <Header size='small' as='h3' mb={2}>
                  {strings.dailyTipText}
                </Header>
                <HTMLText>{dailyTip}</HTMLText>
              </>
            )}
          </div>
        </div>
      </Section>

      {warmUp && (
        <Section className={style.section}>
          <div className={style.content}>
            <div className={style.oneThirdContent}>
              <Header size='medium' as='h2' mb={4}>
                {strings.warmUpText}
              </Header>
              <HTMLText>{warmUp.description}</HTMLText>
            </div>

            <div className={style.twoThirdContent}>
              <Header size='small' as='h3' mb={2}>
                {strings.notesText}
              </Header>
              <HTMLText>{warmUp.notes}</HTMLText>
            </div>
          </div>
        </Section>
      )}

      {workouts &&
        workouts.map((workout) => (
          <Section key={workout.title} className={style.section}>
            <div className={style.content}>
              <div className={style.oneThirdContent}>
                <Header size='medium' as='h2' mb={4}>
                  {workout.title}
                </Header>
                <HTMLText>{workout.description}</HTMLText>
              </div>

              <div className={style.twoThirdContent}>
                {workout.notes && (
                  <Header size='small' as='h3' mb={2}>
                    {strings.notesText}
                  </Header>
                )}

                {workout.notes?.content && (
                  <HTMLText>{workout.notes.content}</HTMLText>
                )}

                {workout.notes?.stimulusAndGoals && (
                  <>
                    <h5>{strings.stimulusGoalsText}</h5>
                    <HTMLText>{workout.notes.stimulusAndGoals}</HTMLText>
                  </>
                )}

                {workout.notes?.strategy && (
                  <>
                    <h5>{strings.strategyText}</h5>
                    <HTMLText>{workout.notes.strategy}</HTMLText>
                  </>
                )}
                {workout.notes?.modifications && (
                  <>
                    <h5>{strings.modificationsText}</h5>
                    <table className={style.table}>
                      <thead>
                        <tr>
                          <th>{strings.movementText}</th>
                          <th>{strings.substitutionText}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workout.notes.modifications.map((modification) => (
                          <tr key={modification.movement}>
                            <td className={style.element}>
                              {modification.movement}
                            </td>
                            <td>{modification.substitution}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>
          </Section>
        ))}

      {stretching && (
        <Section className={style.section}>
          <Header size='medium' as='h2' mb={4}>
            {strings.stretchingText}
          </Header>
          <HTMLText>{stretching.description}</HTMLText>
        </Section>
      )}
    </main>
  )
}

export default CompeteDailyClassPlan
